import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Wallet, CreditCard, RefreshCw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

const fmt = (n) => (n ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'USD' });
const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR') : '—';

export const EmergentCostsSection = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/emergent-costs');
            setData(r.data);
        } catch { /* silencieux */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runSync = async () => {
        setSyncing(true);
        try {
            const r = await api.post('/emergent-costs/sync');
            toast.success(`Synchronisé : ${r.data.receipts_seen} reçus lus, ${r.data.new_entries} nouveaux`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
        } finally { setSyncing(false); }
    };

    const credits = (data?.entries || []).filter(e => e.kind === 'credits');
    const allEntries = data?.entries || [];

    return (
        <div data-testid="emergent-costs-section">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900">Coûts de développement Sokuzai</h2>
                    <p className="text-xs text-slate-500">
                        Factures Emergent (Gmail personnel, libellé « Emergent ») — mise à jour auto lundi &amp; jeudi 07h00
                        {data?.last_sync && ` · Dernière synchro : ${new Date(data.last_sync).toLocaleString('fr-FR')}`}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={runSync} disabled={syncing} data-testid="emergent-costs-sync-btn">
                    {syncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                    Actualiser
                </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-violet-200 bg-violet-50/50">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                            <CreditCard className="w-4 h-4" /> Frais d'abonnement
                        </div>
                        <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="subscription-total">
                            {loading ? '…' : fmt(data?.subscription_total)}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            {(data?.entries || []).filter(e => e.kind === 'subscription').length} paiements
                        </p>
                    </CardContent>
                </Card>
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wide">
                                <Wallet className="w-4 h-4" /> Crédits achetés
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowHistory(v => !v)}
                                className="text-xs text-emerald-700 font-medium flex items-center gap-0.5 hover:underline"
                                data-testid="credits-history-toggle"
                            >
                                Chronologie {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                        <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="credits-total">
                            {loading ? '…' : fmt(data?.credits_total)}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{credits.length} achats</p>
                    </CardContent>
                </Card>
                <Card className="border-slate-300 bg-slate-50">
                    <CardContent className="p-4">
                        <div className="text-slate-600 text-xs font-semibold uppercase tracking-wide">Total investi</div>
                        <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="costs-grand-total">
                            {loading ? '…' : fmt(data?.total)}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">Abonnement + crédits</p>
                    </CardContent>
                </Card>
            </div>
            {showHistory && (
                <Card className="mt-3 border-emerald-200" data-testid="credits-history-panel">
                    <CardContent className="p-0">
                        <div className="max-h-80 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-emerald-50 sticky top-0">
                                    <tr className="text-xs uppercase tracking-wide text-emerald-800">
                                        <th className="text-left px-4 py-2">Date</th>
                                        <th className="text-left px-4 py-2">Type</th>
                                        <th className="text-left px-4 py-2">Détail</th>
                                        <th className="text-left px-4 py-2">N° reçu</th>
                                        <th className="text-right px-4 py-2">Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allEntries.map((e) => (
                                        <tr key={e.id} className="border-t border-slate-100">
                                            <td className="px-4 py-1.5 whitespace-nowrap">{fmtDate(e.date)}</td>
                                            <td className="px-4 py-1.5">
                                                {e.kind === 'subscription' ? (
                                                    <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-100 text-violet-700">Abonnement</span>
                                                ) : (
                                                    <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">Recharge crédits</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-1.5 text-xs text-slate-500">{e.description || '—'}</td>
                                            <td className="px-4 py-1.5 font-mono text-xs text-slate-500">{e.receipt_number || '—'}</td>
                                            <td className="px-4 py-1.5 text-right font-semibold">{fmt(e.amount)}</td>
                                        </tr>
                                    ))}
                                    {allEntries.length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Aucun paiement — cliquez sur Actualiser</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default EmergentCostsSection;
